/**
 * Follow-up cards — the outbox for students who are slipping.
 *
 * Every draft is composed from that student's own figures: their rate,
 * the sessions they missed, the run since they were last marked present.
 * Nothing here is a template with a name dropped in.
 *
 * The buttons do only what they say. WhatsApp and Email open a real
 * prefilled message, Copy puts the text on the clipboard, Edit lets the
 * trainer rewrite it first, and Regenerate switches the register.
 * Nothing is sent on the trainer's behalf and nothing is queued.
 *
 * Two sizes: full, and `compact` for the console column, where the
 * message clamps to three lines and opens on click.
 */

import { useMemo, useState } from 'react';
import { Check, Copy, Mail, MessageSquare, Pencil, RefreshCw, X } from 'lucide-react';

import { Section } from './ui';
import { formatDate } from './useTrainerDashboard';

const PAGE = 4;

/* ── The three registers a trainer writes in, softest first ── */
const TONES = [
  { key: 'warm', label: 'Friendly re-engage' },
  { key: 'direct', label: 'Direct check-in' },
  { key: 'formal', label: 'Attendance warning' },
];

const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || 'there';

/**
 * Where to start, by severity. Someone two sessions down gets a nudge;
 * someone at 20% with a five-session run gets the letter that says the
 * certificate is at stake.
 */
const openingTone = (s) => {
  if (s.rate < 35 || s.missedStreak >= 4) return 2;
  if (s.rate < 50 || s.missedStreak >= 2) return 1;
  return 0;
};

export function draftFor(student, toneKey, trainerName) {
  const who = firstName(student.name);
  const me = trainerName || 'your trainer';
  const subject = student.courseName || student.batchName || 'your course';
  const missed = Math.max(0, student.offered - student.attended);

  if (toneKey === 'warm') {
    return (
      `Hi ${who}, this is ${me} from SevenMentor. ` +
      (student.missedStreak > 0
        ? `I noticed you have not been in the last ${student.missedStreak} ${subject} session${student.missedStreak === 1 ? '' : 's'}. `
        : `I noticed your attendance in ${subject} has slipped to ${student.rate}%. `) +
      `Is something clashing with the batch timing? Tell me what works for you and I will help you catch up on what we covered.`
    );
  }

  if (toneKey === 'direct') {
    return (
      `Hi ${who}, ${me} here from SevenMentor. You have attended ${student.attended} of ${student.offered} ` +
      `${subject} sessions so far, which puts you at ${student.rate}%. I would rather sort out what is blocking you ` +
      `than watch you fall further behind — can we speak for five minutes today or tomorrow and plan the catch-up?`
    );
  }

  return (
    `Hi ${who}, this is ${me} from SevenMentor. Your attendance in ${student.batchName || subject} is ${student.rate}%, ` +
    `below the 60% needed to complete the course. You have missed ${missed} of ${student.offered} sessions` +
    (student.lastSeen ? ` and were last present on ${formatDate(student.lastSeen)}` : '') +
    `. Please reply today with a time we can speak so we can put a recovery plan in place before this affects your certification.`
  );
}

/** The evidence line. States what is true, then what it costs. */
export function reasonFor(student) {
  const missed = Math.max(0, student.offered - student.attended);

  if (student.attended === 0) {
    return `Never marked present across ${student.offered} session${student.offered === 1 ? '' : 's'}. Check the enrolment and the register before chasing — a student in the wrong batch reads exactly like this.`;
  }
  if (student.missedStreak >= 3) {
    return `${student.missedStreak} sessions missed in a row, last present ${formatDate(student.lastSeen)}. A run this long rarely recovers without a call.`;
  }
  if (student.missedStreak > 0) {
    return `Missed the last ${student.missedStreak} session${student.missedStreak === 1 ? '' : 's'} and sitting at ${student.rate}%. Early enough that one message usually brings them back.`;
  }
  return `At ${student.rate}% with ${missed} of ${student.offered} sessions missed — below the 60% completion floor even though they attended the most recent class.`;
}

export function OutreachCard({ student, trainerName, compact = false }) {
  const [toneIndex, setToneIndex] = useState(() => openingTone(student));
  const [editing, setEditing] = useState(false);
  const [override, setOverride] = useState(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(!compact);

  const tone = TONES[toneIndex];
  const draft = override ?? draftFor(student, tone.key, trainerName);

  const digits = String(student.phone || '').replace(/\D/g, '');
  const wa = digits.length === 10 ? `91${digits}` : digits;
  const canWhatsApp = wa.length >= 11;

  const regenerate = () => {
    setOverride(null);
    setToneIndex((i) => (i + 1) % TONES.length);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked outside a secure context. Open the text for
      // selection rather than pretending the copy worked.
      setEditing(true);
      setOpen(true);
    }
  };

  const rateTone = student.rate < 40 ? 'bad' : student.rate < 60 ? 'warn' : 'good';
  const channel = canWhatsApp ? 'whatsapp' : student.email ? 'email' : 'no contact on file';
  const showText = open || editing;

  const primary = canWhatsApp ? (
    <a
      className="td-dbtn primary"
      href={`https://wa.me/${wa}?text=${encodeURIComponent(draft)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      <MessageSquare size={15} /> WhatsApp
    </a>
  ) : student.email ? (
    <a
      className="td-dbtn primary"
      href={`mailto:${student.email}?subject=${encodeURIComponent(
        `${student.courseName || 'Your course'} — attendance`
      )}&body=${encodeURIComponent(draft)}`}
    >
      <Mail size={15} /> Email
    </a>
  ) : (
    <span className="td-dbtn primary disabled" title="No phone or email on this student record">
      <X size={15} /> No contact
    </span>
  );

  return (
    <article className={`td-draft${compact ? ' td-draft-sm' : ''}`}>
      <header className="td-draft-head">
        <h4 className="td-draft-name">{student.name}</h4>
        <p className="td-draft-meta">
          {[student.courseName, student.batchName].filter(Boolean).join(' · ') || 'Batch not recorded'}
        </p>
      </header>

      <div className="td-draft-chips">
        <span className={`td-dotchip td-tone-${rateTone}`}>
          <i /> {student.rate}% attendance
        </span>
        {student.missedStreak > 0 && (
          <span className="td-chip">{student.missedStreak} in a row</span>
        )}
        {!compact && <span className="td-chip">{channel}</span>}
        <span className="td-dotchip td-tone-warn">
          <i /> {tone.label}
        </span>
      </div>

      {editing ? (
        <textarea
          className="td-draft-edit"
          value={draft}
          rows={compact ? 6 : 5}
          onChange={(e) => setOverride(e.target.value)}
          aria-label={`Message to ${student.name}`}
        />
      ) : compact ? (
        <button
          type="button"
          className={`td-draft-body td-draft-toggle${showText ? '' : ' clamp'}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={showText}
        >
          {draft}
        </button>
      ) : (
        <p className="td-draft-body">{draft}</p>
      )}

      <p className={`td-draft-why${compact && !showText ? ' clamp' : ''}`}>
        <b>Why this draft ·</b> {reasonFor(student)}
      </p>

      <div className="td-draft-actions">
        {primary}

        {compact ? (
          <>
            <button type="button" className="td-dbtn icon" onClick={() => setEditing((v) => !v)}
              title={editing ? 'Done editing' : 'Edit the message'} aria-label="Edit the message">
              <Pencil size={15} />
            </button>
            <button type="button" className="td-dbtn icon" onClick={regenerate}
              title={`Switch to: ${TONES[(toneIndex + 1) % TONES.length].label}`} aria-label="Change the tone">
              <RefreshCw size={15} />
            </button>
            <button type="button" className="td-dbtn icon" onClick={copy}
              title="Copy to clipboard" aria-label="Copy to clipboard">
              {copied ? <Check size={15} /> : <Copy size={15} />}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="td-dbtn" onClick={() => setEditing((v) => !v)}>
              <Pencil size={15} /> {editing ? 'Done' : 'Edit'}
            </button>
            <button type="button" className="td-dbtn" onClick={regenerate}
              title={`Switch to: ${TONES[(toneIndex + 1) % TONES.length].label}`}>
              <RefreshCw size={15} /> Regenerate
            </button>
            <button type="button" className="td-dbtn" onClick={copy}>
              {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copied' : 'Copy'}
            </button>
          </>
        )}
      </div>
    </article>
  );
}

export default function OutreachCards({ students = [], trainerName, onOpenTab }) {
  const [shown, setShown] = useState(PAGE);

  // Worst first — the ordering the desk table already uses.
  const queue = useMemo(() => [...students].sort((a, b) => a.rate - b.rate), [students]);

  if (queue.length === 0) return null;

  return (
    <>
      <Section
        title="Ready to send"
        stamp={`${queue.length} student${queue.length === 1 ? '' : 's'}`}
        action="Open attendance →"
        onAction={() => onOpenTab('attendance')}
      />

      <div className="td-drafts">
        {queue.slice(0, shown).map((s) => (
          <OutreachCard key={s.id} student={s} trainerName={trainerName} />
        ))}
      </div>

      {shown < queue.length && (
        <button type="button" className="td-more" onClick={() => setShown((n) => n + PAGE)}>
          Show {Math.min(PAGE, queue.length - shown)} more of {queue.length}
        </button>
      )}
    </>
  );
}
